import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import type { FieldDiff } from "@/hooks/useOnboardingForm";

type FieldSelectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diffs: FieldDiff[];
  onConfirm: (selectedFields: string[]) => void;
};

function FieldSelectionDialog({
  open,
  onOpenChange,
  diffs,
  onConfirm,
}: FieldSelectionDialogProps) {
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedFields(new Set(diffs.map((d) => d.field)));
    }
  }, [open, diffs]);

  const allSelected = selectedFields.size === diffs.length;
  const noneSelected = selectedFields.size === 0;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedFields(new Set());
    } else {
      setSelectedFields(new Set(diffs.map((d) => d.field)));
    }
  };

  const handleToggleField = (field: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedFields));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Câmpuri cu date existente</DialogTitle>
          <DialogDescription>
            Selectează câmpurile pe care dorești să le suprascrii cu datele din
            registre.
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-x-3 px-3 py-2 bg-ds-surface-50 border-b text-xs font-medium text-ds-text-500 uppercase tracking-wider">
            <Checkbox
              checked={allSelected}
              onCheckedChange={handleToggleAll}
              aria-label="Selectează toate"
            />
            <span>Câmp</span>
            <span>Valoare curentă</span>
            <span>Valoare ONRC</span>
          </div>

          <div className="divide-y">
            {diffs.map((diff) => {
              const isSelected = selectedFields.has(diff.field);
              return (
                <div
                  key={diff.field}
                  className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-x-3 px-3 py-2.5 hover:bg-ds-surface-50/50 transition-colors"
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleField(diff.field)}
                    className="cursor-pointer"
                  />
                  <span className="text-xs font-medium text-ds-text-600 truncate">
                    {diff.label}
                  </span>
                  <TouchTooltip
                    content={
                      <p className="text-xs max-w-64">{diff.currentValue}</p>
                    }
                    side="top"
                  >
                    <span className="text-xs text-ds-text-400 line-through truncate cursor-default">
                      {diff.currentValue}
                    </span>
                  </TouchTooltip>
                  <TouchTooltip
                    content={
                      <p className="text-xs max-w-64">{diff.newValue}</p>
                    }
                    side="top"
                  >
                    <div className="flex items-center gap-1 min-w-0 cursor-default">
                      <ArrowRight className="h-3 w-3 text-ds-blue-400 shrink-0" />
                      <span className="text-xs text-navy-900 font-medium truncate">
                        {diff.newValue}
                      </span>
                    </div>
                  </TouchTooltip>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Anulează
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={noneSelected}>
            Completează selectate ({selectedFields.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FieldSelectionDialog;
